import {
  SIGNAL_DECAY_SECONDS,
  VISUAL_SIGNALS,
  type VisualSignalEvent,
  type VisualSignalFrame,
  type VisualSignalName,
  emptySignalFrame,
  isLevelSignal,
} from './VisualSignal.js';

/**
 * The one place the two engines meet.
 *
 * The rhythm side writes; the visual side reads. Neither holds a reference to
 * the other, and this class imports from neither — it knows about numbers and
 * names, which is exactly as much as a boundary should know.
 *
 * Two things make it usable as a boundary rather than just an event emitter:
 *
 *   - **Events survive the frame they happen in.** A hit is a moment, but a
 *     renderer runs at frame rate and may be mid-way through something when it
 *     lands. Every event feeds an envelope that decays over its own time, so a
 *     renderer that only samples levels still sees every event, just smeared.
 *   - **A subscriber cannot break the producer.** Renderers are third-party-ish
 *     by design; the whole point of the plugin structure is that new ones get
 *     written. A throwing subscriber is caught, counted and skipped, because a
 *     bad smoke effect must not be able to stop the music.
 */

export type VisualSignalListener = (event: VisualSignalEvent) => void;

export interface SignalEmit {
  readonly name: VisualSignalName;
  readonly time: number;
  readonly strength?: number;
  readonly detail?: Readonly<Record<string, number | string | boolean>>;
}

export class VisualSignalBus {
  private readonly levels = emptySignalFrame();
  private readonly envelopes = emptySignalFrame();
  private readonly frame = emptySignalFrame();
  private readonly listeners = new Map<VisualSignalName | '*', Set<VisualSignalListener>>();

  /** Faults swallowed on behalf of subscribers, for the diagnostics read-out. */
  private faults = 0;
  private lastFault: string | null = null;
  private emitted = 0;

  /** Sets a meter. Levels persist until overwritten; they do not decay. */
  setLevel(name: VisualSignalName, value: number): void {
    this.levels[name] = Number.isFinite(value) ? value : 0;
  }

  setLevels(values: Partial<Record<VisualSignalName, number>>): void {
    for (const key of Object.keys(values) as VisualSignalName[]) {
      const value = values[key];
      if (value !== undefined) this.setLevel(key, value);
    }
  }

  /**
   * Announces a moment.
   *
   * Raises the signal's envelope and notifies subscribers. `strength` is taken
   * as the envelope's new floor rather than replacing it, so two hits inside one
   * decay do not make the second one quieter than the first.
   */
  emit(signal: SignalEmit): void {
    const strength = clamp01(signal.strength ?? 1);
    this.emitted += 1;
    if (strength > this.envelopes[signal.name]) this.envelopes[signal.name] = strength;

    const event: VisualSignalEvent = {
      name: signal.name,
      time: signal.time,
      strength,
      detail: signal.detail ?? {},
    };
    this.dispatch(this.listeners.get(signal.name), event);
    this.dispatch(this.listeners.get('*'), event);
  }

  private dispatch(set: Set<VisualSignalListener> | undefined, event: VisualSignalEvent): void {
    if (set === undefined) return;
    for (const listener of set) {
      try {
        listener(event);
      } catch (cause) {
        // Counted, not rethrown: see the class docstring.
        this.faults += 1;
        this.lastFault = cause instanceof Error ? cause.message : String(cause);
      }
    }
  }

  /** `'*'` receives every signal, which is what a recorder wants. */
  subscribe(name: VisualSignalName | '*', listener: VisualSignalListener): () => void {
    const set = this.listeners.get(name) ?? new Set<VisualSignalListener>();
    set.add(listener);
    this.listeners.set(name, set);
    return () => {
      set.delete(listener);
    };
  }

  /**
   * Advances every envelope and returns the frame.
   *
   * Linear rather than exponential decay, because linear reaches zero. An
   * exponential envelope leaves a renderer permanently a hair above rest, which
   * shows up as geometry that never quite settles.
   */
  advance(deltaSeconds: number): VisualSignalFrame {
    const delta = deltaSeconds > 0 ? deltaSeconds : 0;
    for (const name of VISUAL_SIGNALS) {
      if (isLevelSignal(name)) {
        this.frame[name] = this.levels[name];
        continue;
      }
      const decay = SIGNAL_DECAY_SECONDS[name];
      const current = this.envelopes[name];
      const next = decay <= 0 ? 0 : current - delta / decay;
      this.envelopes[name] = next > 0 ? next : 0;
      this.frame[name] = this.envelopes[name];
    }
    return this.frame;
  }

  /** The last frame produced by `advance`, without advancing again. */
  get current(): VisualSignalFrame {
    return this.frame;
  }

  /** Levels stay; envelopes and pending moments do not survive a new run. */
  reset(): void {
    for (const name of VISUAL_SIGNALS) {
      this.levels[name] = 0;
      this.envelopes[name] = 0;
      this.frame[name] = 0;
    }
  }

  diagnostics(): {
    readonly emitted: number;
    readonly listeners: number;
    readonly faults: number;
    readonly lastFault: string | null;
  } {
    let listeners = 0;
    for (const set of this.listeners.values()) listeners += set.size;
    return { emitted: this.emitted, listeners, faults: this.faults, lastFault: this.lastFault };
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
